import { Dialog } from '@wg/wows-react-uikit';
import { ItemTypes } from '@wg/wows-entities/const';
import WoWSEntity from '@wg/wows-entities/wrappers/react/WoWSEntity';
import classnames from 'classnames';
import equal from 'fast-deep-equal/react';
import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ShipFeaturesDialog.module.scss';
import type { EducationStep, IReward } from '~/types';
import {
    SCROLL_EVENT_TYPE, useAppDispatch, useAppSelector, useMount, useScroll
} from '~/hooks';
import { RootState } from '~/store';
import { entityType, ship, shipDescription } from '~/wowsEntities';
import Text from '~/Components/Text/Text';
import BackButton from '~/Components/BackButton/BackButton';
import { Button } from '~/Components/Buttons/Buttons';
import FeaturesLayout from '~/Components/Features/FeaturesLayout/FeaturesLayout';
import ClientApi from '~/clientApi';
import { closeShipFeaturesDialog } from '~/store/dialogs';

export interface RewardsDialog_Props {
    shipId: number,
    isVisible: boolean,
    isMainShip: boolean,
    hide: () => void,
}

export interface RewardsDialogState {
    educationSteps: EducationStep[],
    shipPreviewBackground: string | null,
    secondaryShipPreviewBackground: string | null,
    rewards: Array<IReward>,
    isRulesDialogOpen: boolean,
    isWelcomeDialogOpen: boolean,
}

const stateSelector = (state: RootState): RewardsDialogState => {
    return {
        educationSteps: state.app.educationSteps,
        shipPreviewBackground: state.app.shipPreviewBackground,
        secondaryShipPreviewBackground: state.app.secondaryShipPreviewBackground,
        rewards: state.app.rewards,
        isRulesDialogOpen: state.dialogs.isRulesDialogOpen,
        isWelcomeDialogOpen: state.dialogs.isWelcomeDialogOpen,
    };
};

const ShipFeaturesDialog = (props: RewardsDialog_Props) => {
    const {
        isVisible, isMainShip, shipId, hide
    } = props;
    const {
        isRulesDialogOpen,
        isWelcomeDialogOpen,
        rewards,
        educationSteps,
        shipPreviewBackground,
        secondaryShipPreviewBackground,
    } = useAppSelector(stateSelector, equal);

    const overlayBackground = isMainShip
        ? shipPreviewBackground && `url("${shipPreviewBackground}")`
        : secondaryShipPreviewBackground && `url("${secondaryShipPreviewBackground}")`;

    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const isStacked = isRulesDialogOpen || isWelcomeDialogOpen;

    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const contentScrollRef = React.useRef<HTMLDivElement>(null);
    const isScrollAnimating = React.useRef<boolean>(false);
    const [contentScrollOpened, setContentScrollOpened] = React.useState<boolean>(false);

    const scrollSpeed = 25;
    const scrollTransitionDuration = parseInt(styles.scrollTransitionDuration, 10);

    const step = educationSteps[0];
    const finalRewardDescription = rewards[rewards.length - 1]?.description;

    const close = React.useCallback(() => {
        dispatch(closeShipFeaturesDialog());
    }, [dispatch]);

    const toggleScrollOpened = useCallback(() => {
        setContentScrollOpened((isOpened) => {
            if (isOpened) {
                contentScrollRef.current?.scrollTo({
                    behavior: 'smooth',
                    top: 0,
                });
            }
            return !isOpened;
        });
    }, []);

    const applyDelegatedScroll = (deltaY: number) => {
        if (contentScrollRef.current) {
            contentScrollRef.current.scrollTop = (contentScrollRef.current.scrollTop || 0) + deltaY;
        }
    };

    const applyScroll = (e: WheelEvent) => {
        const deltaY = e.deltaY > 0 ? scrollSpeed : -scrollSpeed;

        if (contentScrollRef.current?.scrollTop === 0) {
            if (deltaY > 0 && !contentScrollOpened) {
                isScrollAnimating.current = true;
                setContentScrollOpened(true);
                return;
            }
            if (deltaY < 0 && contentScrollOpened) {
                isScrollAnimating.current = true;
                setContentScrollOpened(false);
                return;
            }
        }

        if (!isScrollAnimating.current) {
            applyDelegatedScroll(deltaY);
        }
    };

    useMount(() => {
        if (!isStacked) {
            ClientApi.setDialogState(true);
            ClientApi.setScrollover(true);
            ClientApi.switchWorldRendererState(false);
        }

        return () => {
            if (!isStacked) {
                ClientApi.setDialogState(false);
                ClientApi.setScrollover(false);
                ClientApi.switchWorldRendererState(true);
            }
        };
    });

    useScroll(
        wrapperRef,
        SCROLL_EVENT_TYPE.WHEEL,
        (e: WheelEvent) => {
            e.stopPropagation();
            applyScroll(e);
        },
        () => {
            isScrollAnimating.current = false;
        },
        [wrapperRef, contentScrollRef, contentScrollOpened],
        scrollTransitionDuration,
    );

    const renderDialog = React.useCallback(() => {
        return (
            <div className={styles.wrapper} ref={wrapperRef}>
                <div className={styles.backTop}>
                    <BackButton onClick={close} />
                </div>
                <div className={styles.content} onClick={toggleScrollOpened}>
                    <div className={styles.header}>
                        <div className={styles.title}>{ship(String(shipId), false)}</div>
                        <div className={styles.subtitle}>{entityType(String(shipId))}</div>
                    </div>
                    <hr className={classnames(styles.divider, styles.noSpace)} />
                    <div
                        className={classnames(styles.contentScroll, {
                            [styles.contentScrollOpened]: contentScrollOpened,
                        })}
                        ref={contentScrollRef}
                    >
                        {isMainShip && (
                            <div className={styles.features}>
                                {step?.features?.map((feature, fIndex) => (
                                    <div key={fIndex} className={styles.feature}>
                                        <div className={styles.featureIcon} style={{ backgroundImage: `url(${feature.image})` }} />
                                        <span className={styles.featureTitle}>{feature.description}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className={styles.description}>{shipDescription(String(shipId))}</div>
                        {isMainShip && finalRewardDescription ? (
                            <>
                                <hr className={styles.divider} />
                                <div className={styles.description}>
                                    <Text str={finalRewardDescription} />
                                </div>
                            </>
                        ) : null}
                        <hr className={styles.divider} />
                        <div className={styles.contentBlock}>
                            <div className={styles.contentBlockTitle}>{t('Полный список особенностей корабля:')}</div>
                            <div className={styles.contentBlockSubTitle}>{t('Основные отличия от кораблей того же класса и уровня')}</div>
                            <div className={styles.featuresLayout}>
                                <FeaturesLayout shipId={Number(shipId)} />
                            </div>
                        </div>
                        <hr className={styles.divider} />
                        <div className={styles.contentBlock}>
                            <div className={styles.contentBlockTitle}>{t('Доступное снаряжение: ')}</div>
                            <div className={styles.consumables}>
                                {
                                    <WoWSEntity
                                        type={ItemTypes.CONSUMABLES}
                                        id={shipId}
                                        presentation={{
                                            withTooltip: true,
                                        }}
                                    />
                                }
                            </div>
                        </div>
                    </div>
                    <span
                        className={classnames(styles.contentScrollBg, {
                            [styles.visible]: contentScrollOpened,
                        })}
                    />
                    <div className={styles.actions}>
                        <Button label={t('Назад')} onClick={close} />
                    </div>
                </div>
            </div>
        );
    }, [shipId, finalRewardDescription, close, contentScrollOpened, isMainShip, step?.features, t, toggleScrollOpened]);

    if (!shipId || !overlayBackground) {
        close();
        return null;
    }

    return (
        <Dialog
            className={classnames(styles.shipFeaturesDialog)}
            content={renderDialog()}
            size={'overlay'}
            isOverlay={false}
            isVisible={isVisible}
            hideDialog={hide}
            overlayBackground={overlayBackground}
        />
    );
};

export default memo(ShipFeaturesDialog);
